/**
 * Authored decision-brief copy, ported from the design handoff's sample dataset
 * (claude_code_files/design_handoff_drilldown_redesign/prototype/dva-data.js).
 *
 * Keyed by opportunity id, NOT by rank. The handoff's dataset renumbered a curated
 * selection as ranks 1–10; five of those records live at other ids in the real fixture.
 * The parenthetical is the CURRENT (library-canonical, see libraryMapping.ts) title for that
 * id — it names the slot the authored content below is keyed to, not a description of the
 * content's own subject, which was authored against an earlier title at each id and predates
 * both the library-subset mapping and whatever title existed before it:
 *
 *   handoff opp-6  → opp-34 (Non-Emergency Medical Transport Coordination & No-Show Mitigation)
 *   handoff opp-7  → opp-38 (Patient Community and Support Networks)
 *   handoff opp-8  → opp-39 (Real-World Evidence Generation and Analytics Suite)
 *   handoff opp-9  → opp-40 (Integrated Chronic Disease Management Ecosystems)
 *   handoff opp-10 → opp-28 (Lab Consumables Demand Sensing & Reagent Waste Minimization)
 *
 * Rank-dependent figures are tokenized ({rank}, {total}, {percentile}) and interpolated in
 * deriveBrief, because rank is computed from live category weights. The {value}/{impl}
 * tokens are still substituted for backward-compat but are no longer quoted in authored
 * prose: the drilldown scores on the 4-dimension composite (see data/adapters), so the
 * legacy 88/85-scale value/impl numerals were dropped from decisionText to avoid drift.
 *
 * TODO: verdict and decisionText should ultimately come from the scoring model rather
 * than authored copy + threshold derivation. See deriveBrief in ./decisionBrief.
 */

import { roiForUseCase } from '../roi/useCaseRoi';
import type { VerdictKey } from '../theme/decisionTokens';
import type { RiskItem } from '../components/RiskCard';

export interface RoadmapPhase {
  phase: string;
  /** Duration eyebrow, e.g. "Weeks 1–6". */
  dur: string;
  desc: string;
}

export interface BriefOverride {
  verdict: VerdictKey;
  decisionText: string;
  summary: string;
  steps: string[];
  context: string;
  opImpact: string;
  risks: RiskItem[];
  roadmap: RoadmapPhase[];
}

const AUTHORED_OVERRIDES: Record<string, BriefOverride> = {
  // uc 1693 — Care Coordination and Transition Management, the re-rooted #1. Prose and risk
  // register grounded in the Care Coordination canon (readmissions, closed-loop transitions,
  // medication reconciliation) and its canon roiEstimate of $11.8M-$22.7M.
  'opp-1': {
    verdict: 'green',
    decisionText:
      "Recommendation: Deploy closed-loop care coordination across care transitions. Rationale: 1. Readmission and ED-revisit reduction is the largest single lever on total cost of care under existing value-based contracts. 2. Structured transition and medication-reconciliation workflows move Stars and HEDIS transitions-of-care measures directly. 3. The ADT and payer-claims connectivity required is already in place, so the initiative starts from a ready data foundation.",
    summary:
      "Projected annual benefit of {annualBenefit} with a 3\u20136 month time-to-value; rank #{rank} of {total}, {percentile}th percentile.",
    steps: [
      "Phase 1: Risk stratification and cohort definition at anchor sites (Month 1-2)",
      "Phase 2: Closed-loop transition and medication-reconciliation workflow build (Month 3-4)",
      "Phase 3: Post-acute and SNF partner onboarding, then scale (Month 5-6)",
    ],
    context:
      "Care coordination sits directly on the stated value-based-care priority: coordinated transitions lift shared-savings and risk-contract performance by reducing avoidable readmissions and ED revisits, and the FY26 board plan names CMS readmission penalties and transitions-of-care quality measures as goals.",
    opImpact:
      "Modeled payback of 3 months; full run-rate impact expected by month 6, with an 8\u201312% reduction in avoidable 30-day readmissions across the targeted cohort.",
    risks: [
      { name: "Interoperability and partner onboarding", severity: "High", likelihood: "Medium", description: "High operational risk: interoperability work and SNF-partner onboarding sit on the critical path. Likelihood: Medium.", mitigation: "Milestone checkpoints and executed BAAs de-risk the ramp; anchor sites go first.", owner: "IT / Integration Lead" },
      { name: "Patient engagement in post-discharge outreach", severity: "Medium", likelihood: "Medium", description: "Medium adoption risk: post-discharge outreach and PCP follow-up depend on patient engagement. Likelihood: Medium.", mitigation: "Multi-channel outreach reduces variance across cohorts; track contact rates weekly.", owner: "Change Management Lead" },
      { name: "Risk-stratification model calibration", severity: "Medium", likelihood: "Low", description: "Medium model risk: risk-stratification models need calibration against local case mix. Likelihood: Low.", mitigation: "Start at anchor sites and validate against observed readmissions before scaling.", owner: "Analytics Lead" },
    ],
    roadmap: [
      { phase: "Phase 1", dur: "Weeks 1\u20138", desc: "Risk stratification and cohort definition at anchor sites (Month 1-2)" },
      { phase: "Phase 2", dur: "Months 3\u20134", desc: "Closed-loop transition and medication-reconciliation workflow build (Month 3-4)" },
      { phase: "Phase 3", dur: "Months 5\u20136", desc: "Post-acute and SNF partner onboarding, then scale (Month 5-6)" },
    ],
  },

  'opp-2': {
    verdict: 'green',
    decisionText:
      "Recommendation: Adopt a phased delivery for Predictive Prescription Analytics, starting with a 90-day limited rollout to validate clinical, operational, and financial outcomes before scaling enterprise-wide. Prioritize high-value cohorts and workflows first; expand after demonstrating measurable impact against agreed KPIs.",
    summary:
      "Projected annual benefit of {annualBenefit} with a 6-month time-to-value; rank #{rank} of {total}, {percentile}th percentile.",
    steps: [
      "Assign an executive sponsor and constitute a cross-functional steering committee.",
      "Commission a data-readiness assessment and integration scoping exercise.",
      "Shortlist implementation approach and confirm delivery capacity for a phased launch.",
    ],
    context:
      "Vision: Build a trusted, enterprise healthcare analytics fabric that delivers timely, equitable, and actionable insights to improve outcomes, reduce avoidable utilization, and strengthen financial performance, without disrupting clinician workflow.",
    opImpact:
      "Modeled payback of 3 months; full run-rate impact expected by month 6, with measurable operational gains in targeted workflows.",
    risks: [
      { name: "Assumption confidence below target", severity: "Medium", likelihood: "Low", description: "Medium financial risk: Assumption confidence below target. Likelihood: Low.", mitigation: "Validate key financial assumptions against client-specific benchmarks. Review and update after Pipeline 1 profile completion.", owner: "Financial Analyst" },
      { name: "User adoption below target", severity: "High", likelihood: "Medium", description: "High change management risk: User adoption below target. Likelihood: Medium.", mitigation: "Target adoption rate 65% requires structured change management, training, and executive sponsorship.", owner: "Change Management Lead" },
      { name: "Implementation timeline dependencies", severity: "Medium", likelihood: "Medium", description: "Medium operational risk: Implementation timeline dependencies. Likelihood: Medium.", mitigation: "Plan for 6-month ramp period. Establish milestone checkpoints and vendor accountability agreements.", owner: "Programme Manager" },
    ],
    roadmap: [
      { phase: "Foundation", dur: "Weeks 1–6", desc: "Data readiness assessment, integration scoping, steering-committee cadence." },
      { phase: "Limited rollout", dur: "Weeks 7–16", desc: "Controlled limited rollout on a high-value workflow with measurable outcomes." },
      { phase: "Scale", dur: "Months 5–9", desc: "System-wide rollout and value reconciliation." },
    ],
  },

  'opp-3': {
    verdict: 'green',
    decisionText:
      "Recommendation: Implement an integrated digital platform that leverages data analytics and machine learning to optimize the specialty referral network.",
    summary:
      "Projected annual benefit of {annualBenefit} with a 6-month time-to-value; rank #{rank} of {total}, {percentile}th percentile.",
    steps: [
      "Phase 1: Planning and Analysis (Month 1-2)",
      "Phase 2: Design and Strategy Development (Month 3-4)",
      "Phase 3: Technology Implementation (Month 5-7)",
    ],
    context:
      "1. Enhancing Patient Experience: - Our core business strategy emphasizes patient-centric care.",
    opImpact:
      "Modeled payback of 2 months; full run-rate impact expected by month 6, with measurable operational gains in targeted workflows.",
    risks: [
      { name: "Implementation timeline dependencies", severity: "Medium", likelihood: "Medium", description: "Medium operational risk: Implementation timeline dependencies. Likelihood: Medium.", mitigation: "Plan for 6-month ramp period. Establish milestone checkpoints and vendor accountability agreements.", owner: "Programme Manager" },
      { name: "User adoption below target", severity: "High", likelihood: "Medium", description: "High change management risk: User adoption below target. Likelihood: Medium.", mitigation: "Target adoption rate 65% requires structured change management, training, and executive sponsorship.", owner: "Change Management Lead" },
      { name: "Assumption confidence below target", severity: "Medium", likelihood: "Low", description: "Medium financial risk: Assumption confidence below target. Likelihood: Low.", mitigation: "Validate key financial assumptions against client-specific benchmarks. Review and update after Pipeline 1 profile completion.", owner: "Financial Analyst" },
    ],
    roadmap: [
      { phase: "Phase 1", dur: "Weeks 1–8", desc: "Phase 1: Planning and Analysis (Month 1-2)" },
      { phase: "Phase 2", dur: "Months 3–4", desc: "Phase 2: Design and Strategy Development (Month 3-4)" },
      { phase: "Phase 3", dur: "Months 5–7", desc: "Phase 3: Technology Implementation (Month 5-7)" },
    ],
  },

  'opp-4': {
    verdict: 'green',
    decisionText:
      "Recommendation: Implement digital tools and platforms that facilitate patient engagement and shared decision-making (SDM) to improve healthcare outcomes and patient satisfaction.",
    summary:
      "Projected annual benefit of {annualBenefit} with a 6-month time-to-value; rank #{rank} of {total}, {percentile}th percentile.",
    steps: [
      "Phase 1: Planning and Research (Month 1-3)",
      "Phase 2: Design and Development (Month 4-9)",
      "Phase 3: Testing and Training (Month 10-12)",
    ],
    context:
      "- Profitable value-based growth: Improves Stars/HEDIS, raises quality bonus revenue, and lowers medical loss ratio through avoided low‑value utilization and better adherence.",
    opImpact:
      "Modeled payback of 3 months; full run-rate impact expected by month 6, with measurable operational gains in targeted workflows.",
    risks: [
      { name: "Implementation timeline dependencies", severity: "Medium", likelihood: "Medium", description: "Medium operational risk: Implementation timeline dependencies. Likelihood: Medium.", mitigation: "Plan for 6-month ramp period. Establish milestone checkpoints and vendor accountability agreements.", owner: "Programme Manager" },
      { name: "User adoption below target", severity: "High", likelihood: "Medium", description: "High change management risk: User adoption below target. Likelihood: Medium.", mitigation: "Target adoption rate 65% requires structured change management, training, and executive sponsorship.", owner: "Change Management Lead" },
      { name: "Assumption confidence below target", severity: "Medium", likelihood: "Low", description: "Medium financial risk: Assumption confidence below target. Likelihood: Low.", mitigation: "Validate key financial assumptions against client-specific benchmarks. Review and update after Pipeline 1 profile completion.", owner: "Financial Analyst" },
    ],
    roadmap: [
      { phase: "Phase 1", dur: "Months 1–3", desc: "Phase 1: Planning and Research (Month 1-3)" },
      { phase: "Phase 2", dur: "Months 4–9", desc: "Phase 2: Design and Development (Month 4-9)" },
      { phase: "Phase 3", dur: "Months 10–12", desc: "Phase 3: Testing and Training (Month 10-12)" },
    ],
  },

  // handoff opp-6
  'opp-34': {
    verdict: 'cond',
    decisionText:
      'Meaningful value, but feasibility is gated by clinical governance overhead; pathway changes need medical staff approval cycles. Approve contingent on a named physician champion and governance fast-track.',
    summary:
      'Variance analytics on high-volume clinical pathways. Rank #{rank} of {total}. Conditional on clinical governance commitment.',
    steps: [
      'Secure physician champion and governance fast-track commitment.',
      'Select 3 first-wave pathways with high variance and strong evidence base.',
      'Baseline variance and cost-per-case by pathway.',
    ],
    context: 'Unwarranted clinical variation drives 8–12% excess cost per case in targeted DRGs at peer systems.',
    opImpact: 'Length-of-stay and supply-cost reduction on first-wave pathways within 2 quarters of adoption.',
    risks: [
      { name: 'Clinical governance pace', severity: 'High', likelihood: 'High', description: 'Pathway changes route through committees meeting monthly.', mitigation: 'Pre-negotiated fast-track for analytics-flagged variances.', owner: 'CMO' },
      { name: 'Physician acceptance', severity: 'Medium', likelihood: 'Medium', description: 'Variance flags can read as performance policing.', mitigation: 'Peer-comparison framing, physician-led rollout.', owner: 'CMO' },
      { name: 'Outcome adjustment validity', severity: 'Medium', likelihood: 'Low', description: 'Risk adjustment must withstand clinical scrutiny.', mitigation: 'Published methodology, transparent model cards.', owner: 'Dir. Data Science' },
    ],
    roadmap: [
      { phase: 'Governance', dur: 'Weeks 1–6', desc: 'Champion, fast-track agreement, pathway selection.' },
      { phase: 'Limited rollout', dur: 'Months 2–7', desc: '3 pathways, variance dashboards, monthly review.' },
      { phase: 'Expand', dur: 'Months 8–12', desc: 'Top 10 DRGs, order-set integration.' },
    ],
  },

  // handoff opp-7
  'opp-38': {
    verdict: 'hold',
    decisionText:
      'Strategic long-term value, but implementation feasibility is the binding constraint: genomic data infrastructure and consent frameworks are not yet in place. Defer 2 quarters; revisit after the research data environment lands.',
    summary: 'Genomic-informed care programs. Rank #{rank} of {total}. Hold: prerequisite infrastructure not in place.',
    steps: [
      'Complete research data environment build (prerequisite).',
      'Establish consent and data-governance framework for genomic data.',
      'Re-score this opportunity in 2 quarters.',
    ],
    context:
      'Precision medicine differentiates specialty programs long-term, but peers that moved before infrastructure readiness saw multi-year delays.',
    opImpact: 'Deferred; no near-term operational impact.',
    risks: [
      { name: 'Infrastructure prerequisite', severity: 'Critical', likelihood: 'High', description: 'No genomic data pipeline or storage architecture exists today.', mitigation: 'Sequence after research data environment; do not start standalone.', owner: 'CIO' },
      { name: 'Consent framework', severity: 'High', likelihood: 'High', description: 'Genomic consent and secondary-use policies undefined.', mitigation: 'Legal + IRB workstream before any data acquisition.', owner: 'Chief Compliance Officer' },
      { name: 'Specialist recruitment', severity: 'Medium', likelihood: 'Medium', description: 'Requires genomic counselors and informatics specialists.', mitigation: 'Partner model with academic affiliate initially.', owner: 'CMO' },
    ],
    roadmap: [
      { phase: 'Prerequisites', dur: 'Quarters 1–2', desc: 'Research data environment, consent framework.' },
      { phase: 'Re-evaluation', dur: 'Quarter 3', desc: 'Re-score with updated feasibility.' },
      { phase: 'Limited rollout (if approved)', dur: 'Quarters 4–6', desc: 'Pharmacogenomics in oncology line.' },
    ],
  },

  // handoff opp-8
  'opp-39': {
    verdict: 'cond',
    decisionText:
      'Credible savings case from energy and space utilization, but sensor coverage is 40% of the main campus. Approve contingent on facilities capex alignment for sensor buildout in the FY27 plan.',
    summary: 'IoT-driven energy and space optimization. Rank #{rank} of {total}. Conditional on sensor capex.',
    steps: [
      'Align sensor buildout with FY27 facilities capex plan.',
      'Limited rollout on the two fully-instrumented buildings.',
      'Baseline energy spend and utilization by zone.',
    ],
    context:
      'Energy is the second-largest non-labor operating cost; peers report 12–18% reduction from analytics-driven optimization.',
    opImpact: 'Deferred maintenance prioritization; measurable energy reduction within 2 quarters on instrumented buildings.',
    risks: [
      { name: 'Sensor coverage gap', severity: 'Medium', likelihood: 'High', description: '60% of main campus lacks telemetry.', mitigation: 'Phase savings claims to instrumented zones; capex-gated expansion.', owner: 'VP Facilities' },
      { name: 'OT/IT integration', severity: 'Medium', likelihood: 'Medium', description: 'Building systems sit on isolated OT networks.', mitigation: 'Read-only data diode pattern approved by security.', owner: 'CISO' },
      { name: 'Savings attribution', severity: 'Low', likelihood: 'Medium', description: 'Weather and census confound energy baselines.', mitigation: 'Normalized baseline methodology agreed with Finance.', owner: 'VP Facilities' },
    ],
    roadmap: [
      { phase: 'Limited rollout', dur: 'Months 1–4', desc: 'Two instrumented buildings, baseline + optimization.' },
      { phase: 'Capex alignment', dur: 'Months 3–6', desc: 'FY27 sensor buildout plan.' },
      { phase: 'Scale', dur: 'Months 7–12', desc: 'Campus-wide rollout as instrumentation lands.' },
    ],
  },

  // handoff opp-9
  'opp-40': {
    verdict: 'cond',
    decisionText:
      'Aligned with VBC strategy and patient-experience goals, but the value case depends on engagement-rate assumptions unvalidated in our population. Approve a limited rollout to validate engagement before full commitment.',
    summary: 'Digital engagement and shared decision-making. Rank #{rank} of {total}. Conditional: validate engagement rates in a limited rollout first.',
    steps: [
      'Define limited-rollout cohort (diabetes, ~2,000 patients).',
      'Validate engagement-rate assumptions against limited-rollout actuals.',
      'Go/no-go on full rollout at day 90 of the limited rollout.',
    ],
    context: 'Engaged patients show 15–20% better adherence, but published engagement rates vary 3x across demographics.',
    opImpact: 'Limited-rollout scale only until engagement rates are confirmed.',
    risks: [
      { name: 'Engagement assumptions', severity: 'High', likelihood: 'Medium', description: 'Value model assumes 35% sustained engagement; our population may differ.', mitigation: '90-day limited rollout with explicit go/no-go threshold (25%).', owner: 'Chief Digital Officer' },
      { name: 'Digital divide', severity: 'Medium', likelihood: 'Medium', description: 'Target cohorts skew older and lower digital access.', mitigation: 'Multi-channel design including telephonic outreach.', owner: 'VP Patient Experience' },
      { name: 'App fatigue', severity: 'Low', likelihood: 'Medium', description: 'Patients already juggle portal + payer apps.', mitigation: 'Integrate into existing portal rather than net-new app.', owner: 'CIO' },
    ],
    roadmap: [
      { phase: 'Limited-rollout design', dur: 'Weeks 1–6', desc: 'Cohort, thresholds, channel mix.' },
      { phase: 'Limited rollout', dur: 'Months 2–5', desc: '2,000-patient diabetes cohort.' },
      { phase: 'Decision', dur: 'Month 6', desc: 'Go/no-go against engagement threshold.' },
    ],
  },

  // handoff opp-10
  'opp-28': {
    verdict: 'hold',
    decisionText:
      'Modest value range and both scores sit in the low band. Data-product revenue is real but premature before governance and de-identification capabilities mature. Defer; revisit with the FY27 data strategy refresh.',
    summary: 'External data-product monetization. Rank #{rank} of {total}. Hold: governance prerequisites and modest value.',
    steps: [
      'Mature de-identification and governance capability (prerequisite).',
      'Track marketplace pricing benchmarks quarterly.',
      'Revisit at FY27 data strategy refresh.',
    ],
    context:
      'Data monetization is a growing revenue line for large systems, but early movers without governance maturity faced compliance findings.',
    opImpact: 'Deferred; no near-term operational impact.',
    risks: [
      { name: 'De-identification maturity', severity: 'High', likelihood: 'High', description: 'Expert-determination capability not yet established.', mitigation: 'Build governance first; certified de-id before any listing.', owner: 'Chief Compliance Officer' },
      { name: 'Reputational exposure', severity: 'Medium', likelihood: 'Low', description: 'Patient data monetization carries public-trust risk.', mitigation: 'Transparent policy, opt-out framework, board review.', owner: 'Chief Compliance Officer' },
      { name: 'Market pricing volatility', severity: 'Low', likelihood: 'Medium', description: 'Data product pricing is immature.', mitigation: 'Benchmark quarterly; no long-term commitments initially.', owner: 'CDO' },
    ],
    roadmap: [
      { phase: 'Prerequisites', dur: 'Quarters 1–3', desc: 'De-id capability, governance framework.' },
      { phase: 'Re-evaluation', dur: 'Quarter 4', desc: 'FY27 data strategy refresh.' },
      { phase: 'Limited listing (if approved)', dur: 'FY27', desc: 'Single de-identified research dataset.' },
    ],
  },
  // uc 1555 — this block is the Real-Time Patient Flow brief. It sat on opp-1 before the
  // Option D re-root moved that slot to Care Coordination; re-keyed to the opportunity it
  // actually describes. Dollars match opp-10's canon roiEstimate ($13.1M–$27.3M).
  'opp-10': {
    verdict: 'green',
    decisionText:
      "Recommendation: Implement a Real-Time Patient Flow and Capacity Management System Rationale: 1. Enhanced Operational Efficiency: Real-time patient flow management allows healthcare facilities to optimize bed utilization, reduce patient wait times, and improve overall operational efficiency.",
    summary:
      "Projected annual benefit of {annualBenefit} with a 3–6 month time-to-value; rank #{rank} of {total}, {percentile}th percentile.",
    steps: [
      "Phase 1: Planning and Requirements Gathering (Month 1-2)",
      "Phase 2: Design and Vendor Selection (Month 3-4)",
      "Phase 3: Development and Customization (Month 5-8)",
    ],
    context:
      "In the quest for operational excellence, Real-Time Patient Flow and Capacity Management aligns seamlessly with our strategic imperatives of enhancing patient care, optimizing resource utilization, and driving financial sustainability.",
    opImpact:
      "Modeled payback of 2 months; full run-rate impact expected by month 6, with measurable operational gains in targeted workflows.",
    risks: [
      { name: "User adoption below target", severity: "High", likelihood: "Medium", description: "High change management risk: User adoption below target. Likelihood: Medium.", mitigation: "Target adoption rate 65% requires structured change management, training, and executive sponsorship.", owner: "Change Management Lead" },
      { name: "Implementation timeline dependencies", severity: "Medium", likelihood: "Medium", description: "Medium operational risk: Implementation timeline dependencies. Likelihood: Medium.", mitigation: "Plan for 6-month ramp period. Establish milestone checkpoints and vendor accountability agreements.", owner: "Programme Manager" },
      { name: "Assumption confidence below target", severity: "Medium", likelihood: "Low", description: "Medium financial risk: Assumption confidence below target. Likelihood: Low.", mitigation: "Validate key financial assumptions against client-specific benchmarks. Review and update after Pipeline 1 profile completion.", owner: "Financial Analyst" },
    ],
    roadmap: [
      { phase: "Phase 1", dur: "Weeks 1–8", desc: "Phase 1: Planning and Requirements Gathering (Month 1-2)" },
      { phase: "Phase 2", dur: "Months 3–4", desc: "Phase 2: Design and Vendor Selection (Month 3-4)" },
      { phase: "Phase 3", dur: "Months 5–8", desc: "Phase 3: Development and Customization (Month 5-8)" },
    ],
  },
};

// ============================================================
// ENGINE PAYBACK IN THE AUTHORED PROSE
//
// Each opImpact line opens "Modeled payback of N months". Those figures were
// typed alongside the ROI model rather than read from it, so an engine change
// left them behind — a brief could claim 3 months while the ROI screen and the
// CFO panel for the same initiative said something else. The number is now
// substituted from the engine; the rest of the authored sentence is untouched.
// ============================================================

const PAYBACK_PROSE = /Modeled payback of \d+ months?/;

export const BRIEF_OVERRIDES: Record<string, BriefOverride> = Object.fromEntries(
  Object.entries(AUTHORED_OVERRIDES).map(([id, override]) => {
    const months = roiForUseCase(id).scenarios.base.paybackMonths;
    if (months == null || !PAYBACK_PROSE.test(override.opImpact)) return [id, override];
    return [
      id,
      {
        ...override,
        opImpact: override.opImpact.replace(
          PAYBACK_PROSE,
          `Modeled payback of ${months} month${months === 1 ? '' : 's'}`,
        ),
      },
    ];
  }),
) as Record<string, BriefOverride>;
